import { Call } from '../../../utils/multicall'
import { aaveV4Spokes, parseAaveV4SpokeLenderKey } from '@1delta/data-sdk'

/**
 * Number of calls per reserve for user data:
 *  0: getUserSuppliedAssets(reserveId, user)
 *  1: getUserDebt(reserveId, user)       → (drawnDebt, premiumDebt)
 *  2: getUserReserveStatus(reserveId, user) → (usingAsCollateral, isBorrowing)
 *  3: getUserPosition(reserveId, user)   → UserPosition struct (includes dynamicConfigKey, shares)
 */
export const USER_CALLS_PER_RESERVE = 4

/**
 * Resolve the spoke entry referenced by a per-spoke V4 lender key.
 * Returns `undefined` if the key isn't a per-spoke key.
 */
function resolveSpokeEntry(chainId: string, lender: string) {
  const parsed = parseAaveV4SpokeLenderKey(lender)
  if (!parsed) return undefined
  return aaveV4Spokes()?.[chainId]?.[parsed.spokeAddrLower]
}

/**
 * Builds multicall call list for Aave V4 user data — single spoke only.
 *
 * The lender argument is a per-spoke synthesized key (`AAVE_V4_<HEX>`).
 * Each spoke is its own venue; we walk its reserves once.
 *
 * Note: the user's per-spoke risk premium is NOT fetched on-chain. It is
 * reproduced off-chain in `createAaveV4UserState` from the same data the
 * Spoke uses (per-position oracle values + per-spoke `collateralRisk` from
 * the public meta), saving one call per spoke per user.
 */
export const buildAaveV4UserCall = (
  chainId: string,
  lender: string,
  account: string,
): Call[] => {
  const spokeEntry = resolveSpokeEntry(chainId, lender)
  if (!spokeEntry) return []

  const spokeAddr = spokeEntry.spoke
  if (!spokeAddr || spokeAddr === '0x' || spokeAddr.length <= 2) return []

  const reserves = spokeEntry.reserves ?? []
  const calls: Call[] = []

  for (const r of reserves) {
    const rid = r.reserveId
    calls.push(
      {
        address: spokeAddr,
        name: 'getUserSuppliedAssets',
        params: [rid, account],
      },
      {
        address: spokeAddr,
        name: 'getUserDebt',
        params: [rid, account],
      },
      {
        address: spokeAddr,
        name: 'getUserReserveStatus',
        params: [rid, account],
      },
      {
        address: spokeAddr,
        name: 'getUserPosition',
        params: [rid, account],
      },
    )
  }

  return calls
}

/**
 * Computes the expected number of calls for the parser to validate.
 */
export const getAaveV4UserExpectedCallCount = (
  chainId: string,
  lender: string,
): number => {
  const spokeEntry = resolveSpokeEntry(chainId, lender)
  if (!spokeEntry) return 0
  const spokeAddr = spokeEntry.spoke
  if (!spokeAddr || spokeAddr === '0x' || spokeAddr.length <= 2) return 0
  return (spokeEntry.reserves?.length ?? 0) * USER_CALLS_PER_RESERVE
}
